import { identityApi, type AccountInfoResponse } from './api';
import { accountInfoSchema } from './schema';

export const ACCOUNT_INFO_KEY = ['identity', 'account-info'] as const;

const STALE_TIME = 5 * 60 * 1000;

export async function fetchAccountInfo(accountID: string): Promise<AccountInfoResponse> {
  // `schema` is the observe-only Pattern-6 contract: a drifted field reports to
  // telemetry and the response passes through untouched (see
  // shared/lib/http/validate-response.ts). Nothing here can throw because of it.
  const res = await identityApi['/account/account-info'](
    { accountID },
    { schema: accountInfoSchema },
  );
  // ACCOUNT_UNSUBSCRIBE = -2: route to /login the same way 50001 does.
  // Lives in the queryFn so any populate path (initial, refetch, invalidate)
  // triggers the side effect, not just the bootstrap one.
  if (res?.state === -2) {
    const { handleAuthError } = await import('@/shared/lib/http/axios');
    void handleAuthError({ reason: 'unsubscribe' });
  }
  // An envelope with no `data` unwraps to undefined; throw so it stays a
  // retryable failure instead of an empty account cached for the stale window.
  if (!res) throw new Error('account-info returned no payload');
  return res;
}

// Shared options factory — used by both useAccountInfoQuery (reactive
// consumers) and queryClient.ensureQueryData (bootstrap awaitable fetch).
export function accountInfoQueryOptions(accountID: string) {
  return {
    queryKey: [...ACCOUNT_INFO_KEY, accountID] as const,
    queryFn: () => fetchAccountInfo(accountID),
    staleTime: STALE_TIME,
    // fetchAccountInfo logs the user out on state === -2 (ACCOUNT_UNSUBSCRIBE).
    // A reconnect-driven refetch could surface a transient -2 mid-session and
    // boot the user. Mutations + bridge invalidate are the only paths that
    // refresh this key.
    refetchOnReconnect: false as const,
  };
}

export { STALE_TIME };
